<script setup lang="ts">
import { onMounted, onUnmounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "@/api/client";
import { authStore } from "@/auth/authStore";
import { getAccount, logoutRedirect } from "@/auth/msal";
import { UnauthorizedError } from "@/api/types";
import StickyJobBar from "@/components/StickyJobBar.vue";
import Toast from "@/components/Toast.vue";

const route = useRoute();
const router = useRouter();

const showShell = computed(() => route.name !== "signin");

async function loadMe() {
  if (!getAccount()) return;
  try {
    const me = await api.me();
    authStore.setMe(me);
    if (!authStore.isAuthorized.value && route.name !== "unauthorized") {
      router.replace({ name: "unauthorized" });
    }
  } catch (e) {
    if (e instanceof UnauthorizedError) {
      router.replace({ name: "signin" });
    }
  }
}

// Refresh /me periodically so the sticky job bar reflects new jobs.
let pollTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  void loadMe();
  pollTimer = setInterval(() => {
    if (!document.hidden) void loadMe();
  }, 15_000);
});
onUnmounted(() => {
  if (pollTimer) clearInterval(pollTimer);
});

// Global Escape closes any open dialog by emitting on document; individual
// dialogs handle it. (Headless UI Dialog already does this.) Nothing to do here.

async function onSignOut() {
  await logoutRedirect();
}
</script>

<template>
  <div class="min-h-full">
    <header
      v-if="showShell"
      class="border-b border-border bg-bg dark:border-border-dark dark:bg-bg-dark"
    >
      <div class="mx-auto flex max-w-7xl items-center gap-6 px-6 py-3 text-sm">
        <router-link to="/" class="font-medium text-fg dark:text-fg-dark">operate-playground</router-link>
        <nav class="flex items-center gap-4 text-fg-muted">
          <router-link to="/" class="hover:text-fg dark:hover:text-fg-dark">Playgrounds</router-link>
          <a
            href="https://github.com/sharedo/operate-playground"
            target="_blank"
            rel="noreferrer noopener"
            class="hover:text-fg dark:hover:text-fg-dark"
          >Docs</a>
        </nav>
        <div class="ml-auto flex items-center gap-3 text-2xs">
          <template v-if="authStore.state.me">
            <span class="mono text-fg dark:text-fg-dark">{{ authStore.state.me.upn }}</span>
            <span
              v-if="authStore.isAdmin.value"
              class="rounded border border-border px-1.5 py-0.5 text-fg-muted dark:border-border-dark"
            >admin</span>
            <button
              type="button"
              class="text-fg-muted hover:underline"
              @click="onSignOut"
            >sign out</button>
          </template>
        </div>
      </div>
    </header>

    <router-view />

    <StickyJobBar v-if="showShell" />
    <Toast />
  </div>
</template>
